import {
    GetFilteredEntriesByPageDocument,
    GetFilteredEntriesByPageQuery,
    GetFilteredEntriesByPageQueryVariables,
} from '@/api/entries/query.generated';
import { useUserStore } from '@/hooks/use-user-store';
import { EventStatus, Result, Status } from '@/types/api.generated';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useClient } from 'urql';

export const filteredEntriesKeys = {
    all: ['filteredEntries'] as const,
    byStatus: (eventStatus?: EventStatus, result?: Result, status?: Status) =>
        [...filteredEntriesKeys.all, eventStatus, result, status] as const,
};

type UseInfiniteFilteredEntriesArgs = {
    eventStatus?: EventStatus;
    result?: Result;
    status?: Status;
};

export const useInfiniteFilteredEntries = ({ eventStatus, result, status }: UseInfiniteFilteredEntriesArgs) => {
    const gqlClient = useClient();
    const guest = useUserStore(state => state.guest);

    return useInfiniteQuery({
        queryKey: filteredEntriesKeys.byStatus(eventStatus, result, status),
        enabled: !guest,
        initialPageParam: 0,
        queryFn: ({ pageParam = 0 }) => {
            return gqlClient
                .query<GetFilteredEntriesByPageQuery, GetFilteredEntriesByPageQueryVariables>(
                    GetFilteredEntriesByPageDocument,
                    {
                        pageable: {
                            page: pageParam,
                            size: 10,
                        },
                        filter: {
                            eventStatus,
                            result,
                            status,
                        },
                    },
                    { requestPolicy: 'network-only' }
                )
                .toPromise();
        },
        getNextPageParam: options => {
            const currentPage = options.data?.getFilteredEntriesByPage?.currentPage ?? 0;
            const totalPages = options.data?.getFilteredEntriesByPage?.totalPages ?? 0;

            if (currentPage + 1 >= totalPages) {
                return undefined;
            }
            return currentPage + 1;
        },
        select: ({ pageParams, pages }) => {
            return {
                pageParams,
                pages: pages.map(page => page.data?.getFilteredEntriesByPage).filter(Boolean),
            };
        },
    });
};
